import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { useState } from "react";
import { generateBlog } from "screens/Admin/AdminDashboardServices";
import type { BlogGenerationRequest } from "screens/Admin/types";
import { getRelativeFontSize, regularFont } from "utils/styles";
import addBlogModalStyles from "./AddBlogModal.styles";

const BRAND = "#f8481c";

/** Match Contact.tsx form field design – same height, centered text/placeholder in both themes */
const INPUT_HEIGHT = 48;
const INPUT_PADDING_Y = 14;

const getInputSx = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    "& .MuiOutlinedInput-root": {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      minHeight: INPUT_HEIGHT,
      height: INPUT_HEIGHT,
      alignItems: "center",
      backgroundColor: isDark
        ? alpha(theme.palette.background.default, 0.5)
        : alpha(theme.palette.background.paper, 0.9),
      borderRadius: "12px",
      padding: "0 14px",
      boxSizing: "border-box",
      "& fieldset": {
        borderWidth: 1.5,
        borderColor: isDark
          ? alpha(theme.palette.divider, 0.5)
          : alpha(theme.palette.common.black, 0.22),
      },
      "&:hover fieldset": {
        borderColor: alpha(BRAND, 0.6),
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND,
        borderWidth: 2,
        boxShadow: `0 0 0 4px ${alpha(BRAND, 0.15)}`,
      },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      padding: `${INPUT_PADDING_Y}px 0`,
      lineHeight: 1.43,
      height: "auto",
      boxSizing: "border-box",
      "&::placeholder": {
        fontSize: getRelativeFontSize(0),
        opacity: 0.8,
        color: theme.palette.text.secondary,
      },
    },
    "& .MuiSelect-select": {
      color: theme.palette.text.primary,
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      padding: `${INPUT_PADDING_Y}px 0`,
      paddingRight: "32px",
      lineHeight: 1.43,
      height: "auto",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
    },
  };
};

const getInputLabelProps = (theme: Theme, value: string) => ({
  shrink: !!value,
  sx: {
    ...regularFont,
    fontSize: getRelativeFontSize(0),
    color: theme.palette.text.secondary,
  },
});

/** Exact copy of Contact.tsx textFieldSx – for Custom title TextField only */
const getContactTextFieldSx = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      backgroundColor: isDark
        ? alpha(theme.palette.background.default, 0.5)
        : alpha(theme.palette.background.paper, 0.9),
      borderRadius: "12px",
      "& fieldset": {
        borderWidth: 1.5,
        borderColor: isDark
          ? alpha(theme.palette.divider, 0.5)
          : alpha(theme.palette.common.black, 0.22),
      },
      "&:hover fieldset": {
        borderColor: alpha(BRAND, 0.6),
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND,
        borderWidth: 2,
        boxShadow: `0 0 0 4px ${alpha(BRAND, 0.15)}`,
      },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      "&::placeholder": {
        fontSize: getRelativeFontSize(0),
        opacity: 0.8,
      },
    },
  };
};

const CATEGORIES = [
  "Hydroponics",
  "Technology",
  "Innovation",
  "Wellness",
  "Finance",
  "Philanthropy",
  "Entrepreneurship",
];

type GenOption = "category_only" | "category_and_title";

interface AddBlogModalProps {
  open: boolean;
  onClose: () => void;
  onGenerated?: (jobId: string) => void;
}

const AddBlogModal = ({ open, onClose, onGenerated }: AddBlogModalProps) => {
  const theme = useTheme();
  const styles = addBlogModalStyles;
  const inputSx = getInputSx(theme);
  const contactTextFieldSx = getContactTextFieldSx(theme);
  const [option, setOption] = useState<GenOption>("category_only");
  const [category, setCategory] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!category.trim()) {
      openErrorNotification("Please select a category.");
      return;
    }
    if (option === "category_and_title" && !customTitle.trim()) {
      openErrorNotification("Please enter a custom title.");
      return;
    }
    setLoading(true);
    try {
      const payload: BlogGenerationRequest = {
        key: category.trim(),
        input_mode: "title",
        ...(option === "category_and_title" && customTitle.trim()
          ? { title: customTitle.trim() }
          : { title: category.trim() }),
      };
      const response = await generateBlog(payload);
      openSuccessNotification(response.message ?? "Blog generation started.");
      onClose();
      setCategory("");
      setCustomTitle("");
    } catch (e: any) {
      handleErrorMessage(e?.message ?? "Failed to start blog generation.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setCategory("");
      setCustomTitle("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-blog-dialog-title"
      PaperProps={{
        sx: styles.paper,
      }}
      BackdropProps={{
        sx: { backdropFilter: "blur(4px)" },
      }}
    >
      <DialogTitle
        id="add-blog-dialog-title"
        sx={styles.header}
        component="div"
      >
        <Typography sx={styles.headerTitle}>Add Blog – Generate</Typography>
        <Box sx={styles.closeButtonWrap}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
            size="small"
            sx={styles.closeButton}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={styles.body}>
        <Typography sx={styles.subtitle}>
          Choose how to generate your blog:
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={styles.optionCard}
            className={option === "category_only" ? "selected" : ""}
            onClick={() => setOption("category_only")}
          >
            <Typography sx={styles.optionTitle}>
              Generate from Category only
            </Typography>
            <Typography sx={styles.optionDesc}>
              AI will generate title and content based on the selected category.
            </Typography>
          </Box>
          <Box
            sx={styles.optionCard}
            className={option === "category_and_title" ? "selected" : ""}
            onClick={() => setOption("category_and_title")}
          >
            <Typography sx={styles.optionTitle}>
              Generate from Category + Custom Title
            </Typography>
            <Typography sx={styles.optionDesc}>
              Provide a title; AI will generate content aligned with category
              and title.
            </Typography>
          </Box>
        </Box>

        <FormControl fullWidth sx={{ mt: 2.5, minWidth: 200, ...inputSx }}>
          <InputLabel
            id="add-blog-category-label"
            shrink={!!category}
            sx={{
              ...regularFont,
              fontSize: getRelativeFontSize(0),
              color: theme.palette.text.secondary,
            }}
          >
            Category *
          </InputLabel>
          <Select
            labelId="add-blog-category-label"
            label="Category *"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <MenuItem
                key={c}
                value={c}
                sx={{
                  ...regularFont,
                  fontSize: getRelativeFontSize(0),
                  color: theme.palette.text.primary,
                }}
              >
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {option === "category_and_title" && (
          <TextField
            variant="outlined"
            fullWidth
            label="Custom title"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            placeholder="Enter blog title"
            sx={{ mt: 2, ...contactTextFieldSx }}
            InputLabelProps={{
              shrink: !!customTitle,
              sx: {
                ...regularFont,
                fontSize: getRelativeFontSize(0),
                color: theme.palette.text.secondary,
              },
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={styles.footer}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading || !category}
          sx={styles.generateButton}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Generate"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBlogModal;
